import Drawable from '../entities/Drawable.js';
import { imageStore } from '../images.js';
import Sprite from './Sprite.js';
import PixelText from './PixelText.js';
import { TOWN_MAP_POSITIONS } from '../data/townMapPositions.js';

// The game town map shows the middle 30x20 window of the full region grid
// at 8 pixels per cell, yielding a 240x160 image. Grid coordinates follow
// the 1-indexed convention used by pkmng-mgmt-v2/app/data/locationMeta.ts
// and pkmng-mgmt-v2/app/components/map/GameMap.vue: columns 31-60 and
// rows 21-40 are shown. Subtracting 1 before multiplying by CELL_SIZE
// matches the `row - 1` / `col - 1` shift that mgmt-v2's GridSquare.vue
// applies before rendering.
const MAP_ORIGIN_COLUMN = 31;
const MAP_ORIGIN_ROW = 21;
const MAP_VIEWPORT_COLUMNS = 30;
const MAP_VIEWPORT_ROWS = 20;
const CELL_SIZE = 8;
// Blink the player marker at ~2 Hz (60fps / 30 frames).
const PLAYER_MARKER_BLINK_FRAMES = 30;
// Cursor input: move immediately on first press, then delay before hold-to-repeat.
const CURSOR_INITIAL_HOLD_FRAMES = 18;
const CURSOR_REPEAT_FRAMES = 6;
// Pixels per frame the cursor sprite drifts toward its target cell. At 60fps
// with a cell size of 8 this lands on the new cell in ~4 frames, a touch
// faster than the repeat cadence so held inputs still feel continuous.
const CURSOR_TWEEN_SPEED = 2;

export function townMapGridToPixel({ row, column }) {
    return {
        x: (column - MAP_ORIGIN_COLUMN) * CELL_SIZE,
        y: (row - MAP_ORIGIN_ROW) * CELL_SIZE,
    };
}

function clampColumn(column) {
    return Math.min(
        MAP_ORIGIN_COLUMN + MAP_VIEWPORT_COLUMNS - 1,
        Math.max(MAP_ORIGIN_COLUMN, column)
    );
}

function clampRow(row) {
    return Math.min(
        MAP_ORIGIN_ROW + MAP_VIEWPORT_ROWS - 1,
        Math.max(MAP_ORIGIN_ROW, row)
    );
}

export default class TownMapMenu extends Drawable {
    constructor() {
        super();
        this.visible = false;
        this.background = new Sprite({
            position: { x: 0, y: 0 },
            image: imageStore.get('img/town_map.png'),
            visible: true,
        });
        this.playerMarker = new Sprite({
            position: { x: 0, y: 0 },
            image: imageStore.get('img/player_head_down.png'),
            visible: true,
        });
        this.cursor = new Sprite({
            position: { x: 0, y: 0 },
            image: imageStore.get('img/town_map_cursor.png'),
            visible: true,
        });
        this.locationNameText = new PixelText({
            text: '',
            color: PixelText.whiteTextColor,
            shadowColor: PixelText.blackShadowColor,
            fontType: 'large',
            position: { x: 8, y: 8 },
        });
        this.currentPosition = null;
        this.cursorPosition = null;
        this._cursorPixel = null;
        this._frameCounter = 0;
        this._locationByGrid = new Map();
        this._locationIndexReady = false;

        this._inputState = {
            up: { pressed: false, holdFrames: 0 },
            down: { pressed: false, holdFrames: 0 },
            left: { pressed: false, holdFrames: 0 },
            right: { pressed: false, holdFrames: 0 },
        };
    }

    setCurrentPosition(position) {
        this.currentPosition = position ?? null;
        this.cursorPosition = position ? { ...position } : null;
        this._cursorPixel = this.cursorPosition
            ? townMapGridToPixel(this.cursorPosition)
            : null;
        this._frameCounter = 0;
    }

    buildLocationIndex(getLocationById) {
        this._locationByGrid.clear();
        Object.keys(TOWN_MAP_POSITIONS).forEach((locationId) => {
            const pos = TOWN_MAP_POSITIONS[locationId];
            const key = `${pos.row},${pos.column}`;
            this._locationByGrid.set(key, locationId);
        });
        this._getLocationById = getLocationById;
        this._locationIndexReady = true;
    }

    getHoveredLocation() {
        if (!this.cursorPosition || !this._locationIndexReady) {
            return null;
        }
        const key = `${this.cursorPosition.row},${this.cursorPosition.column}`;
        const locationId = this._locationByGrid.get(key);
        if (!locationId || !this._getLocationById) {
            return null;
        }
        const location = this._getLocationById(locationId);
        return location ?? null;
    }

    handleCursorInput(keys) {
        if (!this.cursorPosition) {
            return;
        }
        const moveUp = keys.w.pressed || keys.W.pressed;
        const moveDown = keys.s.pressed || keys.S.pressed;
        const moveLeft = keys.a.pressed || keys.A.pressed;
        const moveRight = keys.d.pressed || keys.D.pressed;

        this._stepAxis('up', moveUp, () => {
            this.cursorPosition.row = clampRow(this.cursorPosition.row - 1);
        });
        this._stepAxis('down', moveDown, () => {
            this.cursorPosition.row = clampRow(this.cursorPosition.row + 1);
        });
        this._stepAxis('left', moveLeft, () => {
            this.cursorPosition.column = clampColumn(this.cursorPosition.column - 1);
        });
        this._stepAxis('right', moveRight, () => {
            this.cursorPosition.column = clampColumn(this.cursorPosition.column + 1);
        });
    }

    _stepAxis(direction, isPressed, moveFn) {
        const state = this._inputState[direction];
        if (!isPressed) {
            state.pressed = false;
            state.holdFrames = 0;
            return;
        }
        if (!state.pressed) {
            moveFn();
            state.pressed = true;
            state.holdFrames = 0;
            return;
        }
        state.holdFrames += 1;
        if (state.holdFrames < CURSOR_INITIAL_HOLD_FRAMES) {
            return;
        }
        if ((state.holdFrames - CURSOR_INITIAL_HOLD_FRAMES) % CURSOR_REPEAT_FRAMES === 0) {
            moveFn();
        }
    }

    draw() {
        if (!this.visible) {
            return;
        }
        this.background.draw();
        this._frameCounter += 1;
        if (this.currentPosition) {
            const show =
                Math.floor(this._frameCounter / PLAYER_MARKER_BLINK_FRAMES) % 2 === 0;
            if (show) {
                const pixel = townMapGridToPixel(this.currentPosition);
                this.playerMarker.position = pixel;
                this.playerMarker.draw();
            }
        }
        if (this.cursorPosition) {
            const target = townMapGridToPixel(this.cursorPosition);
            if (!this._cursorPixel) {
                this._cursorPixel = { ...target };
            } else {
                this._cursorPixel.x = stepToward(
                    this._cursorPixel.x,
                    target.x,
                    CURSOR_TWEEN_SPEED
                );
                this._cursorPixel.y = stepToward(
                    this._cursorPixel.y,
                    target.y,
                    CURSOR_TWEEN_SPEED
                );
            }
            this.cursor.position = this._cursorPixel;
            this.cursor.draw();

            const hovered = this.getHoveredLocation();
            this.locationNameText.text = hovered ? hovered.name : '';
            if (this.locationNameText.text) {
                this.locationNameText.draw();
            }
        }
    }
}

function stepToward(current, target, maxStep) {
    const delta = target - current;
    if (Math.abs(delta) <= maxStep) {
        return target;
    }
    return current + Math.sign(delta) * maxStep;
}
